import { FieldType, UUID } from '@indigohive/cogfy-types'
import { CreateChatMiddlewareCommand } from '@indigohive/cogfy-types/endpoints/createChatMiddleware'
import { DeleteChatMiddlewareCommand } from '@indigohive/cogfy-types/endpoints/deleteChatMiddleware'
import { DuplicateChatMiddlewareCommand } from '@indigohive/cogfy-types/endpoints/duplicateChatMiddleware'
import { GetChatMiddlewaresResultData } from '@indigohive/cogfy-types/endpoints/getChatMiddlewares'
import { ReorderChatMiddlewareCommand } from '@indigohive/cogfy-types/endpoints/reorderChatMiddleware'
import { UpdateChatMiddlewareCommand } from '@indigohive/cogfy-types/endpoints/updateChatMiddleware'
import { useMutation } from '@tanstack/react-query'
import { TFunction } from 'i18next'
import { PlusIcon } from 'lucide-react'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, OverlayContent } from '../../../../../../components'
import { ChatMiddlewareIcon } from '../../../../../../components/ChatMiddlewareIcon'
import { TreeView } from '../../../../../../components/TreeView'
import { useChatMiddlewares, useCogfy, useCollections } from '../../../../../../hooks'
import { AddChatMiddlewareContextMenu } from './AddChatMiddlewareContextMenu'
import { ChatMiddlewareContextMenu } from './ChatMiddlewareContextMenu'
import { EditChatMiddlewareForm } from './EditChatMiddlewareForm'

export type ChatMiddlewareListProps = {
  fieldId: UUID
  collectionId: UUID
  fields?: {
    id: UUID
    name: string
    type: FieldType
  }[]
}

type ContextMenuMiddleware = {
  element: HTMLElement
  middleware: GetChatMiddlewaresResultData
}

type ChatMiddlewareTreeItem = Omit<GetChatMiddlewaresResultData, 'children'> & {
  label: ReactNode
  helperText: string
  icon?: ReactNode
  canDropOver: boolean
  disabled?: boolean
  children: ChatMiddlewareTreeItem[] | undefined
  middleware: GetChatMiddlewaresResultData
}

type RenameMiddlewareData = {
  element: Element
  middleware: GetChatMiddlewaresResultData
}

function map (
  middleware: GetChatMiddlewaresResultData,
  ancestorDisabled: boolean,
  t: TFunction<'translation', undefined>
): ChatMiddlewareTreeItem {
  const disabled = ancestorDisabled || !middleware.enabled

  const isGroupLike =
    middleware.type === 'group' ||
    middleware.type === 'downstreamFallback' ||
    middleware.type === 'upstreamFallback' ||
    middleware.type === 'openai.router' ||
    middleware.type === 'bedrock.router'

  return {
    ...middleware,
    label: (
      <div className="flex items-center gap-2">
        <div className="grow truncate">
          {middleware.name}
          {!middleware.enabled && <> ({t('Disabled').toLowerCase()})</>}
        </div>
      </div>
    ),
    helperText: middleware.type,
    icon: <ChatMiddlewareIcon type={middleware.type} size={16} />,
    children: isGroupLike
      ? middleware.children?.map(child => map(child, ancestorDisabled || !middleware.enabled, t)) ?? []
      : undefined,
    canDropOver: isGroupLike,
    middleware,
    disabled
  }
}

export function ChatMiddlewareList (props: ChatMiddlewareListProps) {
  const { fieldId, collectionId } = props

  const { t } = useTranslation()
  const client = useCogfy()
  const [addMiddlewareAnchorEl, setAddMiddlewareAnchorEl] = useState<HTMLElement | null>(null)
  const [contextMenuMiddleware, setContextMenuMiddleware] = useState<ContextMenuMiddleware | null>(null)
  const [selectedMiddleware, setSelectedMiddleware] = useState<GetChatMiddlewaresResultData | null>(null)
  const [searchMiddlewareName, setSearchMiddlewareName] = useState('')
  const [middlewareToRename, setMiddlewareToRename] = useState<RenameMiddlewareData | null>(null)
  const middlewareToRenameWidth = middlewareToRename ? middlewareToRename?.element.getBoundingClientRect().width - 25 : 0
  const [name, setName] = useState<string | undefined>(undefined)
  const getCollections = useCollections()
  const getMiddlewares = useChatMiddlewares(collectionId, fieldId)
  const createMiddleware = useMutation({
    mutationFn: (data: CreateChatMiddlewareCommand) => client.createChatMiddleware(data),
    onSuccess: () => getMiddlewares.refetch()
  })
  const deleteMiddleware = useMutation({
    mutationFn: (data: DeleteChatMiddlewareCommand) => client.deleteChatMiddleware(data),
    onSuccess: () => getMiddlewares.refetch()
  })
  const duplicateMiddleware = useMutation({
    mutationFn: (data: DuplicateChatMiddlewareCommand) => client.duplicateChatMiddleware(data),
    onSuccess: () => getMiddlewares.refetch()
  })
  const reorderMiddleware = useMutation({
    mutationFn: (data: ReorderChatMiddlewareCommand) => client.reorderChatMiddleware(data),
    onSuccess: () => getMiddlewares.refetch()
  })
  const updateMiddleware = useMutation({
    mutationFn: (data: UpdateChatMiddlewareCommand) => client.updateChatMiddleware(data),
    onSuccess: () => getMiddlewares.refetch()
  })
  const update = () => {
    if (middlewareToRename?.middleware.id) {
      updateMiddleware.mutate(
        {
          chatMiddlewareId: middlewareToRename.middleware.id,
          name,
          collectionId,
          fieldId
        },
        {
          onSuccess: () => {
            setMiddlewareToRename(null)
            getMiddlewares.refetch()
          }
        }
      )
    }
  }

  return (
    <div>
      <div className="flex items-center">
        <h3 className="text-sm font-bold grow">
          Middlewares
        </h3>
        <Button
          size="xs"
          color='primary'
          onClick={event => {
            setAddMiddlewareAnchorEl(event.currentTarget)
            setSearchMiddlewareName('')
          }}
        >
          <PlusIcon size={16} />
          <span>{t('editFieldDrawer:Add')}</span>
        </Button>
      </div>
      <TreeView
        items={getMiddlewares.data?.data?.map(middleware => map(middleware, false, t)) ?? []}
        placeholder={{
          label: (
            <span className="opacity-40">
              {t('editFieldDrawer:No middlewares configured')}
            </span>
          )
        }}
        onItemClick={item => {
          setSelectedMiddleware(item.middleware)
        }}
        onContextMenu={(event, item) => {
          setContextMenuMiddleware({ element: event.currentTarget, middleware: item.middleware })
          event.stopPropagation()
        }}
        onDrop={(middleware, over, position) => {
          const chatMiddlewareId = middleware.id
          if (position === 'bottom') {
            reorderMiddleware.mutate({ chatMiddlewareId, afterId: over.id, collectionId, fieldId })
          } else if (position === 'middle') {
            reorderMiddleware.mutate({ chatMiddlewareId, parentId: over.id, collectionId, fieldId })
          } else {
            reorderMiddleware.mutate({ chatMiddlewareId, beforeId: over.id, collectionId, fieldId })
          }
        }}
      />
      <AddChatMiddlewareContextMenu
        anchorEl={addMiddlewareAnchorEl}
        onAddMiddlewareClick={type => {
          createMiddleware.mutate({ fieldId, type })
          setAddMiddlewareAnchorEl(null)
        }}
        onClose={() => setAddMiddlewareAnchorEl(null)}
        searchMiddlewareName={searchMiddlewareName}
        setSearchMiddlewareName={setSearchMiddlewareName}
      />
      <ChatMiddlewareContextMenu
        anchorEl={contextMenuMiddleware?.element}
        middleware={contextMenuMiddleware?.middleware}
        onClose={() => setContextMenuMiddleware(null)}
        onDeleteClick={middleware => {
          setSelectedMiddleware(null)
          deleteMiddleware.mutate({ chatMiddlewareId: middleware.id, collectionId, fieldId })
        }}
        onDuplicateClick={middleware => {
          duplicateMiddleware.mutate({ middlewareId: middleware.id })
        }}
        onDisableClick={middleware => {
          updateMiddleware.mutate({
            chatMiddlewareId: middleware.id,
            enabled: !middleware.enabled,
            collectionId,
            fieldId
          })
        }}
        onRenameClick={middleware => {
          if (contextMenuMiddleware?.element) {
            setMiddlewareToRename({
              element: contextMenuMiddleware?.element,
              middleware
            })
          }
        }}
      />

      {middlewareToRename && (
        <OverlayContent
          open={Boolean(middlewareToRename)}
          anchorEl={middlewareToRename.element}
          anchorPosition={{
            top: middlewareToRename.element.getBoundingClientRect().top + 3,
            left: middlewareToRename.element.getBoundingClientRect().left + 27
          }}
          style={{ width: middlewareToRenameWidth }}
        >
          <Input
            className="w-64 -ml-1 pb-[1px]"
            size="xs"
            autoFocus
            defaultValue={middlewareToRename.middleware.name ?? ''}
            onChange={event => {
              setName(event.target.value)
            }}
            onBlur={update}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                (event.target as HTMLInputElement).blur()
              }
            }}
          />
        </OverlayContent>
      )}

      <div className="divider" />
      {selectedMiddleware && (
        <EditChatMiddlewareForm
          key={selectedMiddleware.id}
          collectionId={collectionId}
          fieldId={fieldId}
          fields={props.fields}
          collections={getCollections.data?.data ?? []}
          middleware={selectedMiddleware}
        />
      )}
      {!selectedMiddleware && (
        <div className="text-sm opacity-40">
          {t('editFieldDrawer:No middleware selected')}
        </div>
      )}
    </div>
  )
}
